import { hideSideBarOptions, showSideBarOptions } from "@modules/bookmarks/entries/sideBarOptions.ts"
import { queryElement, requireElement } from "@modules/bookmarks/html-elements"
import { htmlToElement } from "@modules/bookmarks/html-parser"

export const TagApplyEvent = "bookmarks.tag:apply" as const
export const TagShowOptionsEvent = "bookmarks.tag:show-options" as const

export type TagInstance = InstanceType<typeof Tag>
export type TagEventPayload = {
    tag: TagInstance
}
export type TagEvent = CustomEvent<TagEventPayload>

const tagDefaultChip = (): HTMLLIElement =>
    htmlToElement<HTMLLIElement>(`
    <li id="without-tag" class="tag">
        <lds-button class="apply-tag" size="${SiteInfo.isMobileVersion ? "lg" : "sm"}" data-tag-slug="-">Ohne Tag</lds-button>
    </li>
`)

const tagChipTemplate = (tagName: string, tagSlug: string): HTMLLIElement =>
    htmlToElement<HTMLLIElement>(`
    <li class="tag" data-tag-slug="${tagSlug}" data-tag-name="${tagName}">
        <lds-button class="apply-tag" size="${SiteInfo.isMobileVersion ? "lg" : "sm"}">${tagName}</lds-button>
        <lds-button class="tag-options" size="${SiteInfo.isMobileVersion ? "lg" : "sm"}">
            <svg viewBox="0 0 20 20"><path d="M20 5.804l-10 10-10-10 1.609-1.609 8.391 8.391 8.391-8.391z"></path></svg>
        </lds-button>
    </li>
`)

class Tag {
    public readonly tagElement: HTMLLIElement
    private readonly tagApplyButton: HTMLButtonElement | null
    private readonly tagOptionsButton: HTMLButtonElement | null
    private tagNameInternal: string
    private tagSlugInternal: string

    public constructor(tagName: string, tagSlug: string)
    public constructor(tagName: string, tagSlug: string) {
        this.tagNameInternal = tagName
        this.tagSlugInternal = tagSlug

        if (this.tagNameInternal === "Ohne Tag" || this.tagSlugInternal === "-") {
            this.tagElement = tagDefaultChip()
        } else {
            this.tagElement = tagChipTemplate(this.tagNameInternal, this.tagSlugInternal)
        }

        this.tagApplyButton = requireElement<HTMLButtonElement>(".apply-tag", this.tagElement)
        this.tagOptionsButton = queryElement<HTMLButtonElement>(".tag-options", this.tagElement)

        this.initializeChipEvents()
    }

    public get tagName(): string {
        return this.tagNameInternal
    }

    public set tagName(newTagName: string) {
        this.tagNameInternal = newTagName
        this.tagElement.dataset.tagName = newTagName

        const applyTagButton: Element | null = requireElement(".apply-tag", this.tagElement)

        if (applyTagButton === null) {
            return
        }

        applyTagButton.textContent = newTagName
    }

    public get tagSlug(): string {
        return this.tagSlugInternal
    }

    public set tagSlug(newTagSlug: string) {
        this.tagSlugInternal = newTagSlug
        this.tagElement.dataset.tagSlug = newTagSlug
    }

    public addToList(listElement: HTMLUListElement | null, addBeforeElement: TagInstance | null = null): void {
        listElement?.insertBefore(this.tagElement, addBeforeElement?.getTagRootElement() ?? null)
    }

    public removeFromList(): void {
        this.tagElement.parentElement?.removeChild(this.tagElement)
    }

    public is(tagSlug: string): boolean {
        return this.tagSlugInternal === tagSlug
    }

    public getTagRootElement(): HTMLLIElement {
        return this.tagElement
    }

    private initializeChipEvents(): void {
        this.tagApplyButton?.addEventListener(
            "click",
            (): void => {
                this.removeSelectionFromOtherSelectedTags()
                if (this.tagApplyButton?.parentElement?.classList.contains("tag-selected")) {
                    this.tagApplyButton?.parentElement?.classList.remove("tag-selected")
                } else {
                    this.tagApplyButton?.parentElement?.classList.add("tag-selected")
                }
                this.applyTagToEntryList()
            },
            { passive: true },
        )

        this.tagOptionsButton?.addEventListener("click", (): void => this.openTagOptionsModal(), { passive: true })
    }

    private removeSelectionFromOtherSelectedTags(): void {
        const selectedTagButtons = document.querySelectorAll(".tag-selected")

        for (let i = 0; i < selectedTagButtons.length; i++) {
            const button = selectedTagButtons[i]
            if (button !== this.tagApplyButton?.parentElement) {
                button.classList.remove("tag-selected")
            }
        }
    }
    private applyTagToEntryList(): void {
        this.tagElement.dispatchEvent(
            new CustomEvent(TagApplyEvent, {
                bubbles: true,
                detail: { tag: this } as TagEventPayload,
            }),
        )
    }

    private openTagOptionsModal(): void {
        this.tagElement.dispatchEvent(
            new CustomEvent(TagShowOptionsEvent, {
                bubbles: true,
                detail: { tag: this } as TagEventPayload,
            }),
        )
    }
}

export const makeTag = (tagName: string, tagSlug: string): TagInstance => new Tag(tagName, tagSlug)
export const makeDefaultTag = (): TagInstance => new Tag("Ohne Tag", "-")

// Register Custom Events
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface DocumentEventMap {
        [TagApplyEvent]: TagEvent
        [TagShowOptionsEvent]: TagEvent
    }
    interface HTMLElementEventMap {
        [TagApplyEvent]: TagEvent
        [TagShowOptionsEvent]: TagEvent
    }
}

export function hideAdProviderWhenTagIsSelected(): void {
    document.addEventListener(TagApplyEvent, (event: TagEvent): void => {
        const adClass = SiteInfo.isMobileVersion ? ".page-stripe-page-top-sol" : ".page-stripe-content-solads"
        const ads = document.querySelector("#entry-list")?.querySelectorAll(adClass)
        const hasVisibleCards = document.querySelectorAll(".entry-card:not(.hidden)").length > 0
        const tagSelected = document.querySelectorAll(".tag-selected").length > 0

        if (!ads) return

        const isDefaultTag = event.detail.tag.tagName === "Ohne Tag" && event.detail.tag.tagSlug === "-"

        for (let i = 0; i < ads.length; i++) {
            const ad = ads[i]
            if (!isDefaultTag && tagSelected) {
                ad.classList.add("hidden")
                continue
            }

            if (!hasVisibleCards) {
                ad.classList.add("hidden")
                continue
            }

            ad.classList.remove("hidden")
        }
    })
}

export function showEmptyTagHitsContainer(): void {
    const emptyTagHitsContainer = queryElement<HTMLElement>("#no-hits-for-tag-container")

    if (!emptyTagHitsContainer) {
        return
    }

    emptyTagHitsContainer.classList.remove("hidden")
}

export function showEmptyTagHitsContainerWhenAppliedTagHasNoEntries(): void {
    const emptyTagHitsContainer = queryElement<HTMLElement>("#no-hits-for-tag-container")

    document.addEventListener(TagApplyEvent, (event: TagEvent): void => {
        const hasVisibleCards = document.querySelectorAll(".entry-card:not(.hidden)").length > 0

        if (!hasVisibleCards) {
            emptyTagHitsContainer?.classList.remove("hidden")
            const notHitsTagNameElement = document.getElementById("no-hits-for-tag-name")
            if (notHitsTagNameElement) {
                notHitsTagNameElement.innerHTML = '"' + event.detail.tag.tagName + '"'
                hideSideBarOptions()
            }
        } else {
            emptyTagHitsContainer?.classList.add("hidden")
            showSideBarOptions()
        }
    })
}

export function showNoTagsTextWhenNoTagsExist(): void {
    const ul = document.getElementById("tags-list")

    if (ul) {
        const items = ul.querySelectorAll("li")

        if (items.length > 1) {
            return
        }

        ul.children[0].classList.add("hidden")
        const noTagsTextElement = document.createElement("span")
        noTagsTextElement.innerText =
            "Lege dir bis zu 10 eigene Tags an und weise sie Einträgen zu, um deinen Merkzettel besser zu nutzen."
        ul.appendChild(noTagsTextElement)
    }
}
